import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { heroSlides } from '@/mocks/movies';

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goTo = useCallback((index: number) => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrent(index);
    setTimeout(() => setIsTransitioning(false), 600);
  }, [isTransitioning]);

  useEffect(() => {
    const timer = setInterval(() => {
      goTo((current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [current, goTo]);

  const slide = heroSlides[current];

  return (
    <section className="relative w-full h-[420px] md:h-[600px] overflow-hidden" aria-label="精选推荐">
      {/* Background Images */}
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === current ? 'opacity-100' : 'opacity-0'
          }`}
        >
          <img
            src={s.bgImage}
            alt={s.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/60 via-background-950/30 to-background-50"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-background-950/70 via-transparent to-background-950/40"></div>
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="max-w-xl md:max-w-2xl">
              {/* Genres */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                {slide.genres.map((g) => (
                  <span key={g} className="px-3 py-1 rounded-full bg-primary-500/20 text-primary-300 text-xs font-medium backdrop-blur-sm">
                    {g}
                  </span>
                ))}
                <span className="text-xs text-foreground-400">{slide.year}</span>
              </div>

              {/* Title */}
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold font-heading text-foreground-50 mb-3 leading-tight">
                {slide.title}
              </h1>
              <p className="text-sm md:text-base text-foreground-400 mb-2">{slide.titleEn}</p>

              {/* Tagline */}
              <p className="text-lg md:text-xl text-foreground-200 font-medium mb-4">
                {slide.tagline}
              </p>

              {/* Description */}
              <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6 line-clamp-3 max-w-lg">
                {slide.description}
              </p>

              {/* Actions */}
              <div className="flex items-center gap-4">
                <Link
                  to={`/article/${slide.movieId}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-400 transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-play-fill text-lg"></i>
                  立即观看
                </Link>
                <Link
                  to={`/article/${slide.movieId}`}
                  className="flex items-center gap-2 px-6 py-3 rounded-full bg-foreground-50/10 text-foreground-50 font-medium text-sm hover:bg-foreground-50/20 backdrop-blur-sm transition-all duration-300 whitespace-nowrap"
                >
                  <i className="ri-information-line text-lg"></i>
                  了解详情
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Dots & Arrows */}
      <div className="absolute bottom-8 left-0 right-0 z-20">
        <div className="flex items-center justify-center gap-3">
          {heroSlides.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className={`transition-all duration-400 rounded-full cursor-pointer ${
                i === current
                  ? 'w-6 h-2 bg-primary-500'
                  : 'w-2 h-2 bg-foreground-400/50 hover:bg-foreground-300'
              }`}
              aria-label={`第${i + 1}张幻灯片`}
            />
          ))}
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={() => goTo((current - 1 + heroSlides.length) % heroSlides.length)}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-50/10 hover:bg-foreground-50/20 text-foreground-50 backdrop-blur-sm transition-all duration-300 cursor-pointer"
        aria-label="上一张"
      >
        <i className="ri-arrow-left-s-line text-lg md:text-xl"></i>
      </button>
      <button
        onClick={() => goTo((current + 1) % heroSlides.length)}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-20 w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full bg-foreground-50/10 hover:bg-foreground-50/20 text-foreground-50 backdrop-blur-sm transition-all duration-300 cursor-pointer"
        aria-label="下一张"
      >
        <i className="ri-arrow-right-s-line text-lg md:text-xl"></i>
      </button>
    </section>
  );
}